import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  TemplateRef,
  ViewChild,
  OnDestroy,
  signal,
  computed,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { SKIP_IMPERSONATION } from 'app/core/auth/impersonation.interceptor';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { catchError, debounceTime, finalize, map, of, Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateHelperService } from '../../../../../../shared/services/date-helper.service';
import type { Entry, Module } from '../../../modules.model';
import { ModulesStore } from '../../../modules.store';
import { EntriesService } from '../../entries/entries.service';
import { EntriesHooksService } from '../../entries/entries-hooks.service';
import { UtilityService } from '../../../../../../shared/services/utility.service';
import { OverlayModule } from '@angular/cdk/overlay';
import { PortalModule } from '@angular/cdk/portal';
import { TooltipOverlayDirective } from 'app/shared/directives/tooltip-overlay.directive';
import { RouterLink } from '@angular/router';
import { Action } from '../actions.model';
import { ActionsService, ModulesService } from 'app/api/services';

interface ActionLog {
  id: string;
  moduleId: string;
  module: Module;
  action: string;
  actionEntry: Action;
  actionId: string;
  entryId: string;
  entry: Entry;
  result: string;
  additionalData: { Message: string };
  createdAt: string;
  dateString: string;
  /** Both filled by `actionLogsEnriched`, not by the API. */
  entryLabel?: string;
  entryResolve?: EntryResolveStatus;
}

/**
 * Per-row state of the on-demand entry lookup. `none` = nothing to look up (no entry on
 * this log), `idle` = only the id is known and the lookup button is offered.
 */
const EntryResolveStatusEnum = <const>{
  None: 'none',
  Idle: 'idle',
  Loading: 'loading',
  Resolved: 'resolved',
  Missing: 'missing',
  Forbidden: 'forbidden',
  Error: 'error',
};
type EntryResolveStatus = (typeof EntryResolveStatusEnum)[keyof typeof EntryResolveStatusEnum];

type ResolvedEntry = { status: EntryResolveStatus; name?: string };

/**
 * Cache key of a looked-up entry. Deliberately per module+entry and not per log: one
 * lookup fills in every row pointing at the same entry.
 */
function entryCacheKey(log: ActionLog): string {
  return `${log.moduleId}:${log.entryId}`;
}

/** The label of an entry; linked-field values keep theirs one level deeper. */
function entryDisplayName(entry: Entry | undefined): string | undefined {
  const selectedInput = entry?.selectedInput as string | { selectedInput?: string } | undefined;
  if (!selectedInput) {
    return undefined;
  }
  return typeof selectedInput === 'object' ? selectedInput.selectedInput : selectedInput;
}

@Component({
  selector: 'app-action-watcher',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
    MatChipsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    OverlayModule,
    PortalModule,
    TooltipOverlayDirective,
    RouterLink,
  ],
  template: `
    <div class="p-6 max-h-[90vh] flex flex-col" data-cy="action-watcher">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-bold">
          <mat-icon class="relative top-2" svgIcon="feather:activity"></mat-icon> &nbsp;Actie Log Viewer
        </h2>
        <div class="flex gap-2">
          <button mat-icon-button (click)="refreshLogs()" [disabled]="loading()" matTooltip="Logs verversen">
            <mat-icon>refresh</mat-icon>
          </button>
          <button mat-icon-button (click)="dialogRef.close()" matTooltip="Sluiten">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>

      <div class="mb-4">
        <form [formGroup]="filterForm">
          <!-- Primary Filter Row -->
          <div class="flex justify-between items-center gap-4 mb-3">
            <mat-form-field appearance="outline" class="flex-[0_0_25%]">
              <mat-label>Zoeken</mat-label>
              <input matInput formControlName="smartSearch" placeholder="Zoek op ID's, berichten of trefwoorden..." />
              <mat-icon matIconSuffix>search</mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline" class="flex-[0_0_25%]">
              <mat-label>Action Type</mat-label>
              <mat-select formControlName="actionType">
                <mat-option [value]="''">All Actions</mat-option>
                <mat-option value="entryCreated">Entry Created</mat-option>
                <mat-option value="entryUpdated">Entry Updated</mat-option>
                <mat-option value="entryDeleted">Entry Deleted</mat-option>
                <mat-option value="transformAndSave">Transform And Save</mat-option>
              </mat-select>
              <mat-icon class="icon-size-5" matSuffix svgIcon="feather:chevron-down"></mat-icon>
            </mat-form-field>

            <mat-form-field appearance="outline" class="flex-[0_0_25%]">
              <mat-label>Result Status</mat-label>
              <mat-select formControlName="resultStatus">
                <mat-option [value]="''">All Results</mat-option>
                <mat-option value="processed">Processed</mat-option>
                <mat-option value="failure">Failure</mat-option>
                <mat-option value="pending">Pending</mat-option>
              </mat-select>
              <mat-icon class="icon-size-5" matSuffix svgIcon="feather:chevron-down"></mat-icon>
            </mat-form-field>

            <div class="flex-[0_0_20%] flex gap-2">
              <button mat-stroked-button (click)="toggleAdvancedFilters()" class="flex-1">
                <mat-icon>{{ showAdvancedFilters() ? 'expand_less' : 'tune' }}</mat-icon>
                {{ showAdvancedFilters() ? 'Minder' : 'Meer filters' }}
              </button>
            </div>
          </div>

          <!-- Advanced Filters (Collapsible) -->
          <div
            [class.hidden]="!showAdvancedFilters()"
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-gray-50 rounded border mb-3 transition-all duration-200">
            <mat-form-field appearance="outline">
              <mat-label>Module ID</mat-label>
              <input matInput formControlName="moduleId" placeholder="Specifiek Module ID" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Item ID</mat-label>
              <input matInput formControlName="entryId" placeholder="Specifiek Item ID" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Actie ID</mat-label>
              <input matInput formControlName="actionId" placeholder="Specifiek Actie ID" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Alleen Berichten</mat-label>
              <input matInput formControlName="searchMessage" placeholder="Alleen in berichten zoeken" />
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Datum Vanaf</mat-label>
              <input matInput [matDatepicker]="dateFromPicker" formControlName="dateFrom" />
              <mat-datepicker-toggle matIconSuffix [for]="dateFromPicker"></mat-datepicker-toggle>
              <mat-datepicker #dateFromPicker></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline">
              <mat-label>Datum Tot</mat-label>
              <input matInput [matDatepicker]="dateToPicker" formControlName="dateTo" />
              <mat-datepicker-toggle matIconSuffix [for]="dateToPicker"></mat-datepicker-toggle>
              <mat-datepicker #dateToPicker></mat-datepicker>
            </mat-form-field>
          </div>
        </form>

        <!-- Filter Actions and Status -->
        <div class="flex gap-2 items-center flex-wrap">
          @if (hasActiveFilters()) {
            <button mat-stroked-button (click)="clearFilters()" class="text-sm">
              <mat-icon>clear</mat-icon>
              Alles wissen ({{ activeFilterCount() }})
            </button>
          }
          <div class="text-sm text-gray-500 flex items-center ml-auto">
            <mat-icon class="text-base mr-1">info</mat-icon>
            {{ actionLogsFiltered().length }} van {{ actionLogs().length }} geladen logs getoond
            @if (hasMore()) {
              <span class="ml-1">— scroll voor meer</span>
            }
          </div>
        </div>
      </div>

      <div class="overflow-auto flex-grow" (scroll)="onScroll($event)">
        @if (loading()) {
          <div class="flex justify-center p-8">
            <mat-spinner [diameter]="40"></mat-spinner>
          </div>
        }

        @if (error()) {
          <div class="text-red-500 p-4 rounded bg-red-50 mb-4">
            {{ error() }}
          </div>
        }

        @if (!loading() && actionLogs().length > 0) {
          <table mat-table [dataSource]="actionLogsFiltered()" class="w-full bg-card" [trackByFn]="trackByLogId">
            <ng-container matColumnDef="createdAt">
              <th mat-header-cell *matHeaderCellDef>Timestamp</th>
              <td mat-cell *matCellDef="let log">
                <div class="text-xs font-mono leading-3">{{ log.dateString }}</div>
              </td>
            </ng-container>
            <ng-container matColumnDef="action">
              <th mat-header-cell *matHeaderCellDef>Action type</th>
              <td mat-cell *matCellDef="let log">
                <mat-chip-set>
                  <mat-chip class="text-sm" [ngClass]="getActionColor(log.action)" disableRipple>
                    {{ log.action }}
                  </mat-chip>
                </mat-chip-set>
              </td>
            </ng-container>
            <ng-container matColumnDef="actionId">
              <th mat-header-cell *matHeaderCellDef>Action</th>
              <td mat-cell *matCellDef="let log">
                <a
                  [routerLink]="[
                    '/beheerders-instellingen/',
                    log.module?.type === 'entity' || log.module?.type === 'module' ? 'modules' : 'paginas',
                    log.moduleId,
                    'actions',
                    log.actionId,
                  ]"
                  (click)="dialogRef.close()"
                  class="text-xs font-mono leading-4 cursor-pointer hover:underline"
                  [appTooltip]="log.actionEntry ? log.actionEntry : log.actionId"
                  [tooltipTemplate]="tooltipTemplate">
                  {{ log.actionEntry?.name ?? log.actionId }}
                </a>
              </td>
            </ng-container>

            <ng-container matColumnDef="moduleId">
              <th mat-header-cell *matHeaderCellDef>Module</th>
              <td mat-cell *matCellDef="let log">
                <a
                  [routerLink]="[
                    '/beheerders-instellingen/',
                    log.module?.type === 'entity' || log.module?.type === 'module' ? 'modules' : 'paginas',
                    log.moduleId,
                  ]"
                  (click)="dialogRef.close()"
                  class="text-xs font-mono leading-4 cursor-pointer hover:underline"
                  [appTooltip]="log.module ? log.module : log.moduleId"
                  [tooltipTemplate]="tooltipTemplate">
                  {{ log.module ? log.module.title : log.moduleId }}
                </a>
              </td>
            </ng-container>
            <ng-container matColumnDef="entryId">
              <th mat-header-cell *matHeaderCellDef>Entry</th>
              <td mat-cell *matCellDef="let log">
                <div class="flex items-center gap-1">
                  <a
                    [routerLink]="getEntryRouterLink(log)"
                    class="text-xs font-mono leading-3 truncate max-w-[200px] cursor-pointer hover:underline"
                    [appTooltip]="log.entry ? log.entry : log.entryId"
                    [tooltipTemplate]="tooltipTemplate"
                    (click)="dialogRef.close()">
                    {{ log.entryLabel }}
                  </a>

                  <!-- Only entries of an already-visited module are in the store; the rest
                       show their id until it is looked up on demand. -->
                  @switch (log.entryResolve) {
                    @case ('idle') {
                      <button
                        mat-icon-button
                        class="!h-6 !w-6 !min-h-0 !min-w-0 text-accent hover:text-primary transition-colors"
                        matTooltip="Naam ophalen"
                        (click)="resolveEntry(log)">
                        <mat-icon class="icon-size-4" svgIcon="feather:search"></mat-icon>
                      </button>
                    }
                    @case ('loading') {
                      <mat-spinner [diameter]="14"></mat-spinner>
                    }
                    @case ('missing') {
                      <span class="text-xs text-amber-600">verwijderd</span>
                    }
                    @case ('forbidden') {
                      <span class="text-xs text-amber-600">geen toegang</span>
                    }
                    @case ('error') {
                      <button
                        mat-icon-button
                        class="!h-6 !w-6 !min-h-0 !min-w-0 text-accent hover:text-primary transition-colors"
                        matTooltip="Ophalen mislukt — opnieuw proberen"
                        (click)="resolveEntry(log)">
                        <mat-icon class="icon-size-4" svgIcon="feather:refresh-cw"></mat-icon>
                      </button>
                    }
                  }
                </div>
              </td>
            </ng-container>
            <ng-container matColumnDef="result">
              <th mat-header-cell *matHeaderCellDef>Result</th>
              <td mat-cell *matCellDef="let log">
                <span [class]="log.result === 'processed' ? 'text-green-600' : 'text-amber-600'">
                  {{ log.result }}
                </span>
              </td>
            </ng-container>
            <ng-container matColumnDef="additionalData">
              <th mat-header-cell *matHeaderCellDef>Message</th>
              <td mat-cell *matCellDef="let log">
                <div class="text-xs font-mono leading-4">{{ log.additionalData?.Message }}</div>
              </td>
            </ng-container>
            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>
          </table>
        }

        @if (actionLogsFiltered().length === 0 && !loading()) {
          <div class="text-center p-8 italic text-gray-500">Geen actie logs gevonden met de opgegeven filters.</div>
        }

        <!-- Infinite scroll footer: spinner while appending, manual fallback button when more available -->
        @if (loadingMore()) {
          <div class="flex justify-center p-4">
            <mat-spinner [diameter]="28"></mat-spinner>
          </div>
        } @else if (hasMore() && !loading()) {
          <div class="flex justify-center p-4">
            <button mat-stroked-button (click)="loadMore()">
              <mat-icon>expand_more</mat-icon>
              Meer laden
            </button>
          </div>
        }
      </div>
    </div>
  `,
  styles: `
    ::ng-deep .mat-mdc-select-arrow {
      opacity: 0 !important;
    }

    mat-form-field:focus,
    mat-form-field:active,
    mat-form-field:focus-within {
      mat-icon {
        color: var(--fuse-primary);
      }
    }
  `,
})
export class ActionWatcherComponent implements OnInit, OnDestroy {
  actionsService = inject(ActionsService);
  moduleStore = inject(ModulesStore);
  entriesService = inject(EntriesService);
  dialogRef = inject(MatDialogRef<ActionWatcherComponent>);
  readonly #modulesService = inject(ModulesService);
  readonly #entriesHooksService = inject(EntriesHooksService);

  @ViewChild('tooltipTemplate', { static: true }) tooltipTemplate!: TemplateRef<any>;

  // Calculate 3 days ago as default
  private readonly threeDaysAgo = (() => {
    const date = new Date();
    date.setDate(date.getDate() - 3);
    return date;
  })();

  filterForm = new FormGroup({
    smartSearch: new FormControl(''),
    actionType: new FormControl(''),
    resultStatus: new FormControl(''),
    dateFrom: new FormControl<Date | null>(this.threeDaysAgo), // Default to 3 days ago
    dateTo: new FormControl<Date | null>(null),
    // Keep individual fields for advanced filtering
    moduleId: new FormControl(''),
    entryId: new FormControl(''),
    actionId: new FormControl(''),
    searchMessage: new FormControl(''),
  });

  showAdvancedFilters = signal(false);
  copyButtonIcon = signal('content_copy');

  /**
   * Entries looked up on demand, keyed by `entryCacheKey`. Dialog-scoped on purpose: the
   * logs reach across every module, and writing them into the entries store would seed
   * caches of modules the user never opened.
   */
  resolvedEntries = signal<Record<string, ResolvedEntry>>({});

  actionLogs = signal<ActionLog[]>([]);
  loading = signal(true);
  error = signal<string | null>(null);
  displayedColumns: string[] = ['createdAt', 'action', 'actionId', 'moduleId', 'entryId', 'result', 'additionalData'];
  interval: any;

  // Lazy loading (infinite scroll) state
  readonly batchSize = 50;
  loadedOffset = signal(0); // server-side skip for the next batch
  hasMore = signal(true); // false once a batch returns fewer than batchSize
  loadingMore = signal(false);

  // Form value as signal for reactive filtering
  formValue = signal(this.filterForm.value);

  // Computed filter count and active state
  activeFilterCount = computed(() => {
    const formValue = this.formValue();
    let count = 0;
    if (formValue.smartSearch) count++;
    if (formValue.actionType) count++;
    if (formValue.resultStatus) count++;
    if (formValue.moduleId) count++;
    if (formValue.entryId) count++;
    if (formValue.actionId) count++;
    if (formValue.dateFrom) count++;
    if (formValue.dateTo) count++;
    if (formValue.searchMessage) count++;
    return count;
  });

  hasActiveFilters = computed(() => this.activeFilterCount() > 0);

  /**
   * Logs joined with the module/action/entry they refer to. Kept as a computed instead of
   * enriching once at fetch time: outside beheerders-instellingen the actions cache is
   * still empty when this dialog opens (ngOnInit kicks off the load), so the names have to
   * fill in as soon as the store resolves them.
   */
  actionLogsEnriched = computed<ActionLog[]>(() => {
    const actionsByModule = this.moduleStore.actions() ?? {};
    const entriesByModule = this.moduleStore.entries();
    const resolvedEntries = this.resolvedEntries();

    return this.actionLogs().map(log => {
      const entry = entriesByModule[log.moduleId]?.find(entry => entry.id === log.entryId);
      const resolved = resolvedEntries[entryCacheKey(log)];
      const label = entryDisplayName(entry) ?? resolved?.name;

      return {
        ...log,
        dateString: UtilityService.formatTimeRelative(log.createdAt),
        module: log.moduleId ? this.moduleStore.getModuleById(log.moduleId) : undefined,
        actionEntry: actionsByModule[log.moduleId]?.find(action => action.id === log.actionId),
        entry,
        entryLabel: label ?? log.entryId,
        entryResolve: this.entryResolveStatus(log, label, resolved),
      };
    });
  });

  // Computed filtered logs
  actionLogsFiltered = computed(() => {
    const logs = this.actionLogsEnriched();
    const formValue = this.formValue();

    return logs.filter((entry: ActionLog) => {
      // Smart Search - handles multiple field types
      if (formValue.smartSearch) {
        const searchTerm = formValue.smartSearch.toLowerCase();
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(searchTerm);

        if (isUUID) {
          // UUID pattern - search in ID fields
          const matchesId =
            entry.moduleId?.toLowerCase().includes(searchTerm) ||
            entry.entryId?.toLowerCase().includes(searchTerm) ||
            entry.actionId?.toLowerCase().includes(searchTerm);
          if (!matchesId) return false;
        } else {
          // Text pattern - search in all fields including message
          const message = entry.additionalData?.Message?.toLowerCase() || '';
          const actionName = entry.actionEntry?.name?.toLowerCase() || '';
          const moduleName = entry.module?.title?.toLowerCase() || '';
          const entryName = entry.entry?.selectedInput?.toLowerCase() || '';

          const matchesText =
            message.includes(searchTerm) ||
            actionName.includes(searchTerm) ||
            moduleName.includes(searchTerm) ||
            entryName.includes(searchTerm) ||
            entry.moduleId?.toLowerCase().includes(searchTerm) ||
            entry.entryId?.toLowerCase().includes(searchTerm) ||
            entry.actionId?.toLowerCase().includes(searchTerm);
          if (!matchesText) return false;
        }
      }

      // Action Type filter
      if (formValue.actionType && entry.action !== formValue.actionType) {
        return false;
      }

      // Result Status filter
      if (formValue.resultStatus && entry.result !== formValue.resultStatus) {
        return false;
      }

      // Advanced filters (only when specified)
      if (formValue.moduleId && !entry.moduleId?.toLowerCase().includes(formValue.moduleId.toLowerCase())) {
        return false;
      }

      if (formValue.entryId && !entry.entryId?.toLowerCase().includes(formValue.entryId.toLowerCase())) {
        return false;
      }

      if (formValue.actionId && !entry.actionId?.toLowerCase().includes(formValue.actionId.toLowerCase())) {
        return false;
      }

      // Date range filter
      const entryDate = new Date(entry.createdAt);
      if (formValue.dateFrom && entryDate < formValue.dateFrom) {
        return false;
      }
      if (formValue.dateTo) {
        const dateTo = new Date(formValue.dateTo);
        dateTo.setHours(23, 59, 59, 999); // Include the entire day
        if (entryDate > dateTo) {
          return false;
        }
      }

      // Message-only search filter (advanced)
      if (formValue.searchMessage) {
        const message = entry.additionalData?.Message?.toLowerCase() || '';
        if (!message.includes(formValue.searchMessage.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  });

  // TrackBy function for table performance
  trackByLogId = (_: number, log: ActionLog): string => log.id;

  constructor() {
    // The watcher is reachable from the whole app (icon menu), but only the
    // beheerders-instellingen resolver loads the actions — without this the log rows fall
    // back to raw action ids everywhere else. As an effect because the dialog can open
    // before the modules themselves are in: ensureActionsLoaded needs them and is a no-op
    // until then (and is idempotent afterwards).
    effect(() => {
      if (this.moduleStore.modules().length > 0) {
        this.moduleStore.ensureActionsLoaded();
      }
    });

    let prevDateFrom = this.filterForm.value.dateFrom ?? null;
    this.filterForm.valueChanges.pipe(debounceTime(300), takeUntilDestroyed()).subscribe(value => {
      this.formValue.set(value);
      // `since` is server-side; only a dateFrom change requires re-querying.
      // Other filters are applied client-side over the already-loaded logs.
      const newDateFrom = value.dateFrom ?? null;
      if (newDateFrom?.getTime() !== prevDateFrom?.getTime()) {
        prevDateFrom = newDateFrom;
        this.loadInitial();
      }
    });
  }

  ngOnInit(): void {
    this.loadInitial();
    this.fetchUpdates();
  }

  fetchUpdates(): void {
    this.interval = setInterval(() => {
      this.poll();
    }, 10000); // Poll for newest logs every 10 seconds
  }

  ngOnDestroy(): void {
    clearInterval(this.interval);
  }

  /** Fetch a single batch from the server (since/top/skip), enriched and error-safe. */
  private fetchBatch(skip: number): Observable<ActionLog[]> {
    // Use dateFrom from form, fallback to 3 days ago if not set
    const dateFrom = this.formValue().dateFrom || this.threeDaysAgo;

    return this.actionsService
      .retrieveActionLogs(
        {
          since: DateHelperService.dateTimeToDateAndTime(DateHelperService.toLocalISOString(dateFrom)).date,
          top: this.batchSize,
          skip,
        },
        // Query as the real admin: during an impersonation a 403 here would
        // cancel the takeover.
        new HttpContext().set(SKIP_IMPERSONATION, true)
      )
      .pipe(
        // Raw logs only — module/action/entry are joined in `actionLogsEnriched`.
        map(response => (response ?? []) as unknown as ActionLog[]),
        catchError(err => {
          this.error.set('Failed to load action logs. Please try again.');
          console.error('Error loading action logs:', err);
          return of<ActionLog[]>([]);
        })
      );
  }

  /** Initial / hard reset: load the first batch and replace everything. */
  loadInitial(): void {
    this.error.set(null);
    this.loading.set(true);
    this.fetchBatch(0)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe(batch => {
        this.actionLogs.set(this.sortDesc(batch));
        this.loadedOffset.set(batch.length);
        this.hasMore.set(batch.length === this.batchSize);
      });
  }

  /** Append the next batch (infinite scroll / "Meer laden"). */
  loadMore(): void {
    if (this.loadingMore() || !this.hasMore() || this.loading()) {
      return;
    }
    this.loadingMore.set(true);
    const skip = this.loadedOffset();
    this.fetchBatch(skip)
      .pipe(finalize(() => this.loadingMore.set(false)))
      .subscribe(batch => {
        this.mergeLogs(batch);
        this.loadedOffset.set(skip + batch.length);
        this.hasMore.set(batch.length === this.batchSize);
      });
  }

  /** Poll the newest batch and merge in new entries without disturbing loaded ones. */
  private poll(): void {
    this.fetchBatch(0).subscribe(batch => this.mergeLogs(batch));
  }

  /** Upsert a batch into the loaded set by id, keeping the list sorted newest-first. */
  private mergeLogs(incoming: ActionLog[]): void {
    const byId = new Map(this.actionLogs().map(log => [log.id, log]));
    for (const item of incoming) {
      byId.set(item.id, item);
    }
    this.actionLogs.set(this.sortDesc([...byId.values()]));
  }

  private sortDesc(logs: ActionLog[]): ActionLog[] {
    return logs.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }

  /** Row state for the lookup button: what the enriched log already knows wins. */
  private entryResolveStatus(log: ActionLog, label: string | undefined, resolved?: ResolvedEntry): EntryResolveStatus {
    if (!log.moduleId || !log.entryId) {
      return EntryResolveStatusEnum.None;
    }
    if (label) {
      return EntryResolveStatusEnum.Resolved;
    }
    return resolved?.status ?? EntryResolveStatusEnum.Idle;
  }

  /** Fetch the single entry behind a log row so its name replaces the bare id. */
  resolveEntry(log: ActionLog): void {
    if (!log.moduleId || !log.entryId) {
      return;
    }

    const key = entryCacheKey(log);
    const current = this.resolvedEntries()[key];
    if (current?.status === EntryResolveStatusEnum.Loading || current?.status === EntryResolveStatusEnum.Resolved) {
      return;
    }
    this.setResolvedEntry(key, { status: EntryResolveStatusEnum.Loading });

    this.#modulesService
      .getModuleEntry(
        { moduleId: log.moduleId, entryId: log.entryId },
        // Same reason as the log query itself: a 403 under impersonation would cancel the takeover.
        new HttpContext().set(SKIP_IMPERSONATION, true)
      )
      .subscribe({
        next: response => {
          // The response carries its own moduleId, but fall back to the log's so the
          // title lookup also works when the API leaves it out.
          const entry = { ...response, moduleId: response.moduleId ?? log.moduleId } as Entry;
          this.#entriesHooksService.setDefaultSelectedInputName(entry, this.moduleStore.modules());
          const name = entryDisplayName(entry);

          this.setResolvedEntry(key, {
            // An entry without a title (no titelKeyPath configured) stays on its id —
            // marking it resolved keeps the button from suggesting a retry that changes nothing.
            status: EntryResolveStatusEnum.Resolved,
            name,
          });
        },
        error: (error: HttpErrorResponse) => {
          console.error('Error loading entry for action log:', error);
          this.setResolvedEntry(key, { status: this.resolveErrorStatus(error) });
        },
      });
  }

  private resolveErrorStatus(error: HttpErrorResponse): EntryResolveStatus {
    switch (error.status) {
      case 404:
        return EntryResolveStatusEnum.Missing;
      case 401:
      case 403:
        return EntryResolveStatusEnum.Forbidden;
      default:
        return EntryResolveStatusEnum.Error;
    }
  }

  private setResolvedEntry(key: string, value: ResolvedEntry): void {
    this.resolvedEntries.update(resolved => ({ ...resolved, [key]: value }));
  }

  // Infinite scroll: load the next batch when nearing the bottom of the container
  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const threshold = 200; // px from the bottom
    if (el.scrollHeight - el.scrollTop - el.clientHeight < threshold) {
      this.loadMore();
    }
  }

  getEntryRouterLink(log: ActionLog): any[] | null {
    // Logs can reference a module the current user cannot see; null disables the link.
    if (!log.module?.slug) {
      return null;
    }

    const baseRoute = [log.module.slug];

    // Only add the second parameter if it's not null
    if ((log.module.type === 'entity' || log.module.type === 'module') && log.entryId) {
      baseRoute.push(log.entryId);
    }

    return baseRoute;
  }

  refreshLogs(): void {
    this.loadInitial();
  }

  toggleAdvancedFilters(): void {
    this.showAdvancedFilters.update(value => !value);
  }

  clearFilters(): void {
    this.filterForm.reset({
      smartSearch: '',
      actionType: '',
      resultStatus: '',
      moduleId: '',
      entryId: '',
      actionId: '',
      dateFrom: this.threeDaysAgo, // Reset to default 3 days ago
      dateTo: null,
      searchMessage: '',
    });
    // A dateFrom change here is picked up by the valueChanges subscription, which reloads.
    this.showAdvancedFilters.set(false);
  }

  getActionColor(action: string): string {
    switch (action) {
      case 'entryCreated':
      case 'transformAndSave':
        return 'bg-blue-400';
      case 'entryUpdated':
        return 'bg-blue-600';
      case 'entryDeleted':
        return 'bg-blue-800';
      default:
        return '';
    }
  }
}
